import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import mapContextToProps from 'react-context-toolbox/mapContextToProps';
import { FormattedMessage, defineMessages } from 'react-intl';

import { AuthContext } from '../components/AuthWrapper';
import Card from '../components/Card';
import ContactCardFooter from '../components/ContactCardFooter';
import Form from '../components/Form';
import Heading from '../components/Heading';
import HeroImageSegment from '../components/HeroImageSegment';
import Interstitial, {
  InterstitialForm,
  InterstitialHeader,
} from '../components/Interstitial';
import PageRoot from '../components/PageRoot';
import { CLOUD_WEBSITE_URL } from '../config';
import loginSchema from '../schema/login';

import styles from '../styles/responsive.module.scss';

let forgotPasswordUrl = '';
if (typeof window !== 'undefined') {
  forgotPasswordUrl = `${CLOUD_WEBSITE_URL}/-/forgot-password?redirect_uri=${encodeURIComponent(
    window.location.origin,
  )}`;
}

const serverErrors = defineMessages({
  invalid_grant: {
    id: 'login.invalid_grant',
    defaultMessage:
      'This email/password combination cannot be found in our system',
  },
});

// @reach/Router checks autoFocus
const autoFocus = ref => {
  ref?.focus();
};

const propTypes = {
  login: PropTypes.func.isRequired,
};

class LoginPage extends React.Component {
  state = {
    errors: undefined,
  };

  componentDidMount() {
    const { loggedIn, location } = this.props;

    if (loggedIn)
      navigate(
        location.state && location.state.lastLocation
          ? location.state.lastLocation
          : '/',
        { replace: true },
      );
  }

  handleSubmit = async ({ email, password }) => {
    const { login, location } = this.props;
    try {
      await login(email, password);
    } catch (err) {
      this.handleError({
        password: [serverErrors[err.code] || 'Unknown Error'],
      });
      return;
    }
    navigate(
      location.state && location.state.lastLocation
        ? location.state.lastLocation
        : '/',
      { replace: true },
    );
  };

  handleError = errors => {
    this.setState({ errors });
  };

  render() {
    return (
      <PageRoot theme="light" noFooter>
        <HeroImageSegment />
        <Interstitial>
          <Card.Body>
            <InterstitialHeader>
              <Heading level="1">
                <FormattedMessage id="login.header" defaultMessage="Log in" />
              </Heading>
            </InterstitialHeader>
            <InterstitialForm
              schema={loginSchema}
              errors={this.state.errors}
              onError={this.handleError}
              submitForm={this.handleSubmit}
            >
              <Form.FieldGroup
                name="email"
                type="email"
                label={
                  <FormattedMessage id="login.email" defaultMessage="Email" />
                }
                inputRef={autoFocus}
                alwaysShowLabel
              />

              <Form.FieldGroup
                name="password"
                type="password"
                label={
                  <FormattedMessage
                    id="login.password"
                    defaultMessage="Password"
                  />
                }
                alwaysShowLabel
              />

              <Form.SubmitButton>
                <FormattedMessage id="login.submit" defaultMessage="Log In" />
              </Form.SubmitButton>
              <div className={styles.textAlignCenter}>
                <a href={forgotPasswordUrl}>
                  <FormattedMessage
                    id="login.forgotPassword"
                    defaultMessage="Forgot Password?"
                  />
                </a>
              </div>
            </InterstitialForm>
          </Card.Body>
          <ContactCardFooter />
        </Interstitial>
      </PageRoot>
    );
  }
}

LoginPage.propTypes = propTypes;

export default mapContextToProps(
  AuthContext,
  ({ login, tokenResponse }) => ({ login, loggedIn: !!tokenResponse }),
  LoginPage,
);
